import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Badge } from '@/components/elements/badge'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Separator } from '@/components/elements/separator'
import { Skeleton } from '@/components/elements/skeleton'
import { MultiSelect } from '@/components/MultiSelect'
import { RosterUpload } from '@/components/RosterUpload'
import { SlidePanel } from '@/components/SlidePanel'
import { getStudentEntityCertificates } from '@/services/entity_student_certificates.service'
import { unregisterForEvent, getEvent, registerForEvent } from '@/services/events.service'
import { UserStoreContext } from '@/stores/UserStore'
import { formatDate, formatTimeRange } from '@/utils/formatters'
import { toast } from '@/utils/helpers'

const EventSlideoutView = observer(
  ({ viewEvent, resetAndClose, studentId, canManage = false }) => {
    // Context
    const { activeEntity, memberRoleForActiveEntity } = useContext(UserStoreContext)
    const studentRoleId = studentId ?? memberRoleForActiveEntity?.id
    const form = useForm({
      defaultValues: {
        eventId: viewEvent.id,
        studentCertificates: [],
      },
    })

    // Data Loading

    // Load event details to ensure we have the expanded data
    const { data: event, isPending } = useQuery({
      queryKey: ['events', viewEvent.id],
      queryFn: () => getEvent(viewEvent.id),
    })

    const registeredCertificates = event?.studentCertificates.filter(
      (cert) => cert.student.id === studentRoleId,
    )
    const isRegisteredForEvent = registeredCertificates?.length > 0

    const { data: userCertificates } = useQuery({
      queryKey: ['accounts', 'students', 'certificates', activeEntity.id, studentRoleId],
      queryFn: () =>
        getStudentEntityCertificates({
          entityId: activeEntity,
          studentId: memberRoleForActiveEntity.id,
        }),
      enabled: !isRegisteredForEvent,
    })

    const matchedCertificates = useMemo(() => {
      if (!userCertificates || !event) return []
      const eventCertIds = event.certificates.map((cert) => cert.id)
      return userCertificates.results.filter(
        (cert) => eventCertIds.includes(cert.certificate.id) && cert.status !== 'Issued',
      )
    }, [userCertificates, event])

    useEffect(() => {
      if (matchedCertificates.length === 0) return
      form.setValue(
        'studentCertificates',
        matchedCertificates.map((cert) => cert.id),
      )
    }, [form, matchedCertificates])

    // Data Processing
    const handleError = (m) => toast(m, 'error')
    const handleSuccess = (m) => toast(m, 'success')
    const queryClient = useQueryClient()
    const { isPending: registrationPending, mutate: mutateEvent } = useMutation({
      mutationFn: (data) => registerForEvent(data.eventId, data.studentCertificates),
      onError: (err) => handleError(err.message),
      onSuccess: () => {
        handleSuccess('You have successfully registered for this event.')
        // Refresh events on calendar
        queryClient.invalidateQueries({ queryKey: ['events'] })
        resetAndClose()
      },
    })

    const { isPending: cancellationPending, mutate: mutateCancelation } = useMutation({
      mutationFn: () =>
        unregisterForEvent(
          viewEvent.id,
          registeredCertificates.map((c) => c.id),
        ),
      onError: (err) => handleError(err.message),
      onSuccess: () => {
        handleSuccess('You have successfully cancelled the registration for this event.')
        // Refresh events on calendar
        queryClient.invalidateQueries({ queryKey: ['events'] })
        // Refresh student certificates
        queryClient.invalidateQueries({ queryKey: ['accounts', 'students', 'certificates'] })
        // Refresh instructor view of certificates
        queryClient.invalidateQueries({ queryKey: ['accounts', 'instructors', 'certificates'] })
        resetAndClose()
      },
    })

    /**
     * Handles form submission
     * @param {object} data
     */
    const onSubmit = async (data) => {
      mutateEvent(data)
    }
    if (isPending) {
      return (
        <SlidePanel
          title={viewEvent.name || 'Event'}
          loading
          setOpen={resetAndClose}
          content={
            <div className="flex flex-col gap-3">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          }
        />
      )
    }
    return (
      <SlidePanel
        title={event.name}
        loading={registrationPending || cancellationPending || isPending}
        actions={[
          {
            label: !isRegisteredForEvent ? 'Cancel' : 'Close',
            onClick: () => resetAndClose(),
            type: 'cancel',
          },
          ...(!isRegisteredForEvent
            ? [
                {
                  label: 'Register',
                  onClick: form.handleSubmit(onSubmit),
                  type: 'submit',
                },
              ]
            : []),
          ...(isRegisteredForEvent
            ? [
                {
                  asChild: true,
                  label: (
                    <ConfirmButton
                      onClick={mutateCancelation}
                      confirmTitle="Cancel registration?"
                      confirmContent="Are you sure you want to cancel this registration?"
                    >
                      Cancel Registration
                    </ConfirmButton>
                  ),
                  type: 'delete',
                },
              ]
            : []),
        ]}
        content={
          <div className="flex flex-col gap-6">
            {isRegisteredForEvent && <Badge variant="success">Registered</Badge>}
            <div className="text-muted-foreground flex gap-4 text-sm">
              <CalendarIcon className="h-6 w-6" />
              <div className="flex flex-col gap-2">
                <p>
                  <strong>Date: </strong> {formatDate(event.date)}
                </p>
                <p>
                  <strong>Time: </strong> {formatTimeRange(event.startTime, event.endTime)}
                </p>
              </div>
            </div>

            {canManage && <RosterUpload event={event} />}

            {/* eslint-disable-next-line react/no-danger */}
            <p dangerouslySetInnerHTML={{ __html: event.description }} />
            <Separator />
            <section>
              <h3>Included Trainings</h3>
              <p>
                This event covers all trainings needed to complete the hands-on requirement for the
                following courses:
              </p>
              <ul className="list-disc pl-5">
                {event.certificates.map((cert) => (
                  <li key={cert.id}>{cert.name}</li>
                ))}
              </ul>
              {event.additionalTrainings.length > 0 && (
                <>
                  <p>This event also includes the following additional trainings:</p>
                  <ul className="list-disc pl-5">
                    {event.additionalTrainings.map((training) => (
                      <li key={training.id}>{training.name}</li>
                    ))}
                  </ul>
                </>
              )}
            </section>
            <Separator />
            <p>
              <strong>Note:</strong> You must complete all online course material before taking the
              hands-on training.
            </p>
            {!isRegisteredForEvent && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  {matchedCertificates.length === 0 && (
                    <p className="text-sm text-red-600">
                      You do not have any of the required certificates to register for this event.
                      Please contact your administrator if you believe this is an error.
                    </p>
                  )}
                  {matchedCertificates.length === 1 && (
                    <input type="hidden" {...form.register('studentCertificates')} />
                  )}
                  {matchedCertificates.length > 1 && (
                    <FormField
                      control={form.control}
                      name="studentCertificates"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Course(s)</FormLabel>
                          <FormDescription>
                            Multiple of your courses are covered by this training event. Please
                            indicate which course(s) to apply this training to.
                          </FormDescription>
                          <FormControl>
                            <MultiSelect
                              options={matchedCertificates.map((cert) => ({
                                label: cert.certificate.name,
                                id: cert.id,
                              }))}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <input type="hidden" value={event.id} {...form.register('eventId')} />
                </form>
              </Form>
            )}
          </div>
        }
      />
    )
  },
)

EventSlideoutView.propTypes = {
  viewEvent: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
  studentId: PropTypes.string,
  canManage: PropTypes.bool,
}

export default EventSlideoutView
